import React, { useState } from 'react';
import AdminShell from '../../../components/admin/AdminShell';
import { useCampaign, useCampaignRecipients, useStartCampaign, usePauseCampaign, useResumeCampaign } from '../../../hooks/useMarketingQueries';
import { exportRecipientsToCSV } from '../../../lib/csvExport';
import { toast } from 'sonner';
import { Play, Pause, ArrowLeft, RefreshCcw, Users, Send, CheckCircle2, XCircle, Download } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useNavigate, useParams } from 'react-router-dom';

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  
  const startCampaign = useStartCampaign();
  const pauseCampaign = usePauseCampaign();
  const resumeCampaign = useResumeCampaign();
  
  // Adaptive polling: only poll when campaign is running
  const { data: campaign, isLoading, refetch } = useCampaign(id, {
    refetchInterval: (data) => {
      // Poll every 5 seconds only if status is 'running'
      return data?.status === 'running' ? 5000 : false;
    },
  });
  
  const recipientParams = {
    status: statusFilter === 'all' ? undefined : statusFilter,
  };
  
  const { data: recipients = [] } = useCampaignRecipients(id, recipientParams);

  const handleStart = () => {
    if (!campaign?.segment_id && (campaign?.total_recipients || 0) === 0) {
      toast.error('No segment selected and no recipients loaded for this campaign');
      return;
    }
    startCampaign.mutate({ 
      id, 
      segment_id: campaign?.segment_id,
      current_status: campaign?.status,
      total_recipients: campaign?.total_recipients || 0,
    });
  };
  
  const handlePause = () => {
    pauseCampaign.mutate(id);
  };
  
  const handleResume = () => {
    resumeCampaign.mutate(id);
  };
  
  const handleExportRecipients = () => {
    exportRecipientsToCSV(recipients, campaign?.name, `${campaign?.name}_recipients.csv`);
    toast.success('Recipients exported to CSV');
  };

  const statusBadge = (status) => {
    const styles = {
      draft: 'bg-[rgba(185,185,185,0.12)] border-[rgba(185,185,185,0.22)] text-[#B9B9B9]',
      scheduled: 'bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.22)] text-[#F1E2B8]',
      running: 'bg-[rgba(61,220,151,0.12)] border-[rgba(61,220,151,0.22)] text-[#BFF3D2]',
      paused: 'bg-[rgba(241,200,75,0.12)] border-[rgba(241,200,75,0.22)] text-[#F1E2B8]',
      completed: 'bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.22)] text-[#F1E2B8]',
      failed: 'bg-[rgba(255,77,77,0.12)] border-[rgba(255,77,77,0.22)] text-[#FFD7D7]'
    };
    return (
      <Badge className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs border ${styles[status] || styles.draft}`} data-testid="campaign-status-badge">
        <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
        {status}
      </Badge>
    );
  };

  const recipientStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[rgba(185,185,185,0.12)] border-[rgba(185,185,185,0.22)] text-[#B9B9B9]',
      sent: 'bg-[rgba(212,168,67,0.12)] border-[rgba(212,168,67,0.22)] text-[#F1E2B8]',
      delivered: 'bg-[rgba(61,220,151,0.12)] border-[rgba(61,220,151,0.22)] text-[#BFF3D2]',
      failed: 'bg-[rgba(255,77,77,0.12)] border-[rgba(255,77,77,0.22)] text-[#FFD7D7]',
      skipped: 'bg-[rgba(111,111,111,0.12)] border-[rgba(111,111,111,0.22)] text-[#6F6F6F]',
    };
    return (
      <Badge className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs border ${styles[status] || styles.pending}`}>
        {status}
      </Badge>
    );
  };

  if (isLoading && !campaign) {
    return (
      <AdminShell title="Campaign">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminShell>
    );
  }

  const analytics = {
    sent: campaign?.total_sent || 0,
    delivered: campaign?.total_delivered || 0,
    failed: campaign?.total_failed || 0,
    pending: campaign?.total_pending || 0,
  };
  const deliveryRate = analytics.sent > 0 ? Math.round((analytics.delivered / analytics.sent) * 100) : 0;

  return (
    <AdminShell 
      title={campaign?.name || 'Campaign'}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/marketing/campaigns')}
            className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          {recipients.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportRecipients}
              className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40 gap-2"
              data-testid="export-recipients-button"
              aria-label="Export recipients to CSV"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
          )}
          {(campaign?.status === 'draft' || campaign?.status === 'scheduled') && (
            <Button
              onClick={handleStart}
              disabled={startCampaign.isPending}
              className="bg-[#D4A843] text-black hover:brightness-110 gap-2"
              data-testid="campaign-start-button"
            >
              <Play size={16} />
              {startCampaign.isPending ? 'Starting...' : 'Start Campaign'}
            </Button>
          )}
          {campaign?.status === 'running' && (
            <Button
              onClick={handlePause}
              disabled={pauseCampaign.isPending}
              variant="outline"
              className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] gap-2"
              data-testid="campaign-pause-button"
            >
              <Pause size={16} />
              {pauseCampaign.isPending ? 'Pausing...' : 'Pause'}
            </Button>
          )}
          {campaign?.status === 'paused' && (
            <Button
              onClick={handleResume}
              disabled={resumeCampaign.isPending}
              className="bg-[#D4A843] text-black hover:brightness-110 gap-2"
              data-testid="campaign-resume-button"
            >
              <Play size={16} />
              {resumeCampaign.isPending ? 'Resuming...' : 'Resume'}
            </Button>
          )}
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="icon"
            className="bg-transparent border-[#2A2A2A] text-[#B9B9B9]"
            data-testid="campaign-refresh-button"
            aria-label="Refresh campaign data"
          >
            <RefreshCcw size={16} />
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Status */}
        <div className="flex items-center gap-3">
          {statusBadge(campaign?.status)}
          <span className="text-xs text-[#6F6F6F]">
            Created {campaign?.created_at ? new Date(campaign.created_at).toLocaleDateString() : 'N/A'}
          </span>
        </div>

        {/* Analytics KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#111111] border-[#2A2A2A]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Users size={16} className="text-[#B9B9B9]" />
                <p className="text-xs text-[#B9B9B9] uppercase tracking-wider">Total Recipients</p>
              </div>
              <p className="text-2xl font-['EB_Garamond'] font-semibold text-[#F2F2F2]">{campaign?.total_recipients || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111111] border-[#2A2A2A]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <Send size={16} className="text-[#B9B9B9]" />
                <p className="text-xs text-[#B9B9B9] uppercase tracking-wider">Sent</p>
              </div>
              <p className="text-2xl font-['EB_Garamond'] font-semibold text-[#F2F2F2]">{analytics.sent || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111111] border-[#2A2A2A]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 size={16} className="text-[#BFF3D2]" />
                <p className="text-xs text-[#B9B9B9] uppercase tracking-wider">Delivered</p>
              </div>
              <p className="text-2xl font-['EB_Garamond'] font-semibold text-[#BFF3D2]">{analytics.delivered || 0}</p>
              <p className="text-xs text-[#6F6F6F] mt-1">{deliveryRate}% delivery rate</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111111] border-[#2A2A2A]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-2">
                <XCircle size={16} className="text-[#FF4D4D]" />
                <p className="text-xs text-[#B9B9B9] uppercase tracking-wider">Failed</p>
              </div>
              <p className="text-2xl font-['EB_Garamond'] font-semibold text-[#FF4D4D]">{analytics.failed || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Recipients Table */}
        <Card className="bg-[#111111] border-[#2A2A2A]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#F2F2F2] font-['EB_Garamond']">Recipients</CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px] bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[#F2F2F2]" data-testid="recipient-status-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="recipient-status-table">
                <thead className="border-b border-[#2A2A2A]">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Phone</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Status</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Retry Count</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wider text-[#B9B9B9] font-medium">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E1E1E]">
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 4 }).map((_, j) => (
                          <td key={j} className="px-3 py-2">
                            <div className="h-3 bg-[#202020] rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : recipients.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-[#6F6F6F] text-sm">
                        No recipients loaded yet. Start the campaign to load recipients from the selected segment.
                      </td>
                    </tr>
                  ) : (
                    recipients.map((recipient, idx) => (
                      <tr key={idx} className="hover:bg-[#161616] transition-colors">
                        <td className="px-3 py-2">
                          <p className="text-sm text-[#F2F2F2] font-mono">{recipient.phone_e164}</p>
                        </td>
                        <td className="px-3 py-2">{recipientStatusBadge(recipient.status)}</td>
                        <td className="px-3 py-2 text-right">
                          <p className="text-sm text-[#B9B9B9] font-mono">{recipient.retry_count || 0}</p>
                        </td>
                        <td className="px-3 py-2">
                          <p className="text-xs text-[#6F6F6F]">{recipient.updated_at ? new Date(recipient.updated_at).toLocaleString() : 'N/A'}</p>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminShell>
  );
}
